/**
 * One-off "Backfill…" dialog (PRD §1 profiles-table kebab, §5.1 #16 `backfillMonths`).
 *
 * Picks a window in months and enqueues a single deeper scrape for one profile — the run executes
 * as a backfill (notifications seed silently) and the global `backfillMonths` setting is untouched.
 * Mounted only while open, so the draft is seeded once at mount from the global setting.
 *
 * "Start backfill" fires the mutation and **closes immediately** (redesign [D], same as Scrape
 * now) — feedback is the row's status badge and the queue counter, not a toast.
 */

import { useState } from 'react';
import type { ProfileRowDTO } from '@lw/shared';
import { Modal } from '../../components';
import { useSettingsQuery } from '../settings/useSettings';
import { useStartScrape } from './useHomeData';

const MIN_MONTHS = 1;
const MAX_MONTHS = 120;

export type BackfillDialogProps = {
  profile: ProfileRowDTO;
  onClose: () => void;
};

export function BackfillDialog({ profile, onClose }: BackfillDialogProps) {
  const settingsMonths = useSettingsQuery().data?.backfillMonths ?? 12;
  const [draft, setDraft] = useState(() => String(settingsMonths));
  const scrape = useStartScrape();

  const parsed = Number(draft);
  const invalid =
    draft.trim().length === 0 ||
    !Number.isInteger(parsed) ||
    parsed < MIN_MONTHS ||
    parsed > MAX_MONTHS;

  const submit = () => {
    if (invalid) return;
    // Fire and close — never wait for the response (redesign § Dialogs).
    scrape.mutate({ profileIds: [profile.id], backfillMonths: parsed });
    onClose();
  };

  return (
    <Modal
      open
      title={`Backfill — ${profile.name ?? profile.slug}`}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="table-action" onClick={onClose}>
            <span className="table-action__label">Cancel</span>
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={invalid}>
            Start backfill
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label className="field__label" htmlFor="backfill-months">
            Months to fetch
          </label>
          <input
            id="backfill-months"
            className="input"
            type="number"
            min={MIN_MONTHS}
            max={MAX_MONTHS}
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
          />
          {invalid ? (
            <span className="settings-bad">
              Enter a whole number between {MIN_MONTHS} and {MAX_MONTHS}.
            </span>
          ) : (
            <span className="field__hint">
              One-off run: fetches this profile&rsquo;s posts back {draft} months. The global
              backfill setting (currently {settingsMonths}) is not changed.
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default BackfillDialog;
